import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { ICFProgressScreenEnhanced } from '../../src/screens/progress/ICFProgressScreenEnhanced';
import { useTabAccess } from '../../src/hooks/useTabAccess';
import { useAccessPolicy } from '../../src/hooks/useAccessPolicy';
import { useTranslation } from '../../src/hooks/useTranslation';
import { Text } from '../../src/components/ui/text';
import { WebAppShell } from '../../src/design-system/components';
import { reviewDesk } from '../../src/design-system';
import { ROUTES } from '../../src/navigation/routes';
import UpgradeModal from '../../src/components/billing/UpgradeModal';

export default function ProgressRoute() {
  const router = useRouter();
  const { t } = useTranslation();
  const policy = useAccessPolicy();
  const { showProgress } = useTabAccess();
  const [upgradeVisible, setUpgradeVisible] = useState(false);

  if (!showProgress) {
    const isSchoolManaged = policy.isB2BUser;
    const title = isSchoolManaged
      ? t('icfProgress.lockedSchoolTitle')
      : t('icfProgress.lockedTitle');
    const copy = isSchoolManaged
      ? t(
          'icfProgress.lockedSchoolCopy'
        )
      : t(
          'icfProgress.lockedCopy'
        );
    const canAct = policy.isB2C || policy.canManageSchoolBilling;
    const cta = policy.canManageSchoolBilling
      ? t('icfProgress.lockedSchoolCta')
      : t('icfProgress.lockedCta');
    const onPress = () => {
      if (policy.canManageSchoolBilling) {
        router.replace(ROUTES.TABS_SCHOOL_MANAGEMENT as any);
        return;
      }
      // B2C: open the in-app upgrade paywall instead of dumping the user on Profile.
      setUpgradeVisible(true);
    };

    return (
      <WebAppShell active="progress" contentMaxWidth="100%">
        <View
          style={styles.lockedCard}
          nativeID="progress_overview"
          testID="progress_overview"
        >
          <View style={styles.lockedIcon}>
            <MaterialIcons name="lock-outline" size={32} color={reviewDesk.accent} />
          </View>
          <Text style={styles.lockedTitle}>{title}</Text>
          <Text style={styles.lockedCopy}>{copy}</Text>
          {canAct ? (
            <TouchableOpacity style={styles.primaryButton} onPress={onPress} accessibilityRole="button">
              <Text className="text-white" style={styles.primaryButtonText}>{cta}</Text>
              <MaterialIcons name="arrow-forward" size={18} color="#FFFFFF" />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={() => router.replace(ROUTES.TABS_DASHBOARD as any)}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryButtonText}>{t('navigation.home')}</Text>
            </TouchableOpacity>
          )}
          <UpgradeModal
            visible={upgradeVisible}
            requiredTier="tier2"
            featureKey="icfProgress"
            onClose={() => setUpgradeVisible(false)}
          />
        </View>
      </WebAppShell>
    );
  }
  return <ICFProgressScreenEnhanced />;
}

const styles = StyleSheet.create({
  lockedCard: {
    width: '100%',
    minHeight: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  lockedIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
    marginBottom: 18,
  },
  lockedTitle: {
    color: reviewDesk.ink,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: '800',
    textAlign: 'center',
  },
  lockedCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 520,
    marginTop: 10,
    marginBottom: 22,
  },
  primaryButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 9,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  secondaryButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    color: reviewDesk.accent,
    fontSize: 15,
    fontWeight: '800',
  },
});
